import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

import { PageTypeStats } from "~/api/Api";
import { Icon } from "~/assets/icons/Icon";
import { useTeam } from "~/contexts/TeamProvider";
import { UNLIMITED_PAGE_SCALE } from "~/helpers/consts";

import { apiClient } from "../../api/ApiClient";
import NoData from "../containers/NoData";
import FormLabel from "../login/formLabel/FormLabel";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Switch from "../switch/Switch";
import RangeSlider from "../ui/rangeSlider/RangeSlider";
import Spinner from "../ui/spinner/Spinner";
import { SiteWithAuth } from "./General";
import type { SiteSettingsFormValues } from "./SiteSettings";
import styles from "./SiteSettings.module.scss";

const formatLabel = (type: string) => {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

type SchemaSettingsProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
  /** null when the plan has no page cap */
  pageMax: number | null;
};

const SchemaSettings = ({
  siteData,
  isLoading,
  pageMax,
}: SchemaSettingsProps) => {
  const { id } = useParams();
  const { team } = useTeam();
  const navigate = useNavigate();
  const canUpdateSite = siteData?.authorizations?.member
    ?.flat()
    .includes("update");
  const { control, setValue, watch } = useFormContext<SiteSettingsFormValues>();

  const schemaPageTypes = watch("schema_page_types");
  const schemaSwitchEnabled = watch("ai_schema");
  const aiSchemaOverride = watch("ai_schema_override");

  const isSchemaDisabledByTeam = !team?.ai_schema;
  const effectiveSchemaSwitchEnabled = isSchemaDisabledByTeam
    ? false
    : schemaSwitchEnabled;

  const aiSchemaOverrideLimitTitle = () => {
    if (aiSchemaOverride === -1) {
      return "unlimited";
    }

    return aiSchemaOverride;
  };

  const handleSliderChange = (newValue: number) => {
    const value = newValue === 0 ? -1 : newValue;
    setValue("ai_schema_override", value, { shouldDirty: true });
  };

  const { data: pagesStatusesDetail, isLoading: pagesStatusesDetailIsLoading } =
    useQuery({
      queryKey: ["pagesTypesDetail", id],
      queryFn: async () => {
        const response = await apiClient.sites.pagesTypesDetail(Number(id), {
          q: {
            status_eq: "completed",
          },
        });

        return response?.data?.page_type_stats;
      },
    });

  const availableTypes: (keyof PageTypeStats)[] = useMemo(
    () =>
      !isLoading && pagesStatusesDetail
        ? Object.entries(pagesStatusesDetail)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([_key, value]) => value > 0)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .map(([key, _value]) => key as keyof PageTypeStats)
        : [],
    [isLoading, pagesStatusesDetail],
  );

  const handleTypeSelect = (type: string) => {
    const currentTypes = Array.isArray(schemaPageTypes)
      ? [...schemaPageTypes]
      : [];
    const isSelected = currentTypes.includes(type);

    const newTypes = isSelected
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    setValue("schema_page_types", newTypes, { shouldDirty: true });
  };

  // Custom handler to detect same-tab vs new-tab clicks
  const handleLinkClick = (e: React.MouseEvent, type: string) => {
    // If any of these are true, it's a "new tab" request, so do nothing
    // and let the normal link behavior handle it with the URL parameters
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
      return;
    }

    // Otherwise it's a normal click that would navigate in the same tab
    e.preventDefault();

    // Navigate programmatically with state
    navigate(`/sites/${id}/pages`, {
      state: { selectedPageType: type },
    });
  };

  if (isLoading || pagesStatusesDetailIsLoading) return <Spinner />;

  return (
    <div className={styles.schemaSection}>
      <p className={styles.settingsSectionText}>
        Allows you to customize Schema settings for your website. By selecting
        categories, you can specify for which types of pages Schema will be
        generated.
      </p>

      <div>
        <div className={styles.settingsSwitchGroup}>
          <div>
            <FormLabel label="Generate JSON Schemas for pages" />
            <p className={styles.settingsSectionText}>
              Set the limits for the JSON schemas usage. Current limit{" "}
              {aiSchemaOverrideLimitTitle()}
            </p>
            {isSchemaDisabledByTeam && (
              <div className={styles.settingsNotificationWrapper}>
                <NotificationBlock type="warning">
                  JSON Schemas are disabled, you can enable this feature in{" "}
                  <Link to="/settings/ai">Team settings</Link>.
                </NotificationBlock>
              </div>
            )}
          </div>
          <Controller
            name="ai_schema"
            control={control}
            render={({ field }) => (
              <Switch
                disabled={!canUpdateSite || isSchemaDisabledByTeam}
                checked={effectiveSchemaSwitchEnabled}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        {effectiveSchemaSwitchEnabled && (
          <div className={styles.settingsSectionLeftGroup}>
            <div className={styles.settingsSliderContainer}>
              <RangeSlider
                value={aiSchemaOverride === -1 ? 0 : aiSchemaOverride}
                defaultValue={aiSchemaOverride === -1 ? 0 : aiSchemaOverride}
                onChange={handleSliderChange}
                min={0}
                max={pageMax ?? undefined}
                scaleStops={pageMax === null ? UNLIMITED_PAGE_SCALE : undefined}
                step={100}
                disabled={!canUpdateSite || isSchemaDisabledByTeam}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.settingsSectionGroup}>
        <div>
          <FormLabel label="Schema Limitations" />
          <p className={styles.settingsSectionText}>
            Select page categories to generate schema for
          </p>
        </div>
        <NotificationBlock type="info">
          Note: categories are detected during crawl. A new crawl might be
          required after enabling the JSON Schema.
        </NotificationBlock>
        <div className={styles.schemaCheckboxes}>
          {availableTypes.length === 0 && <NoData title="No Available Pages" />}
          {availableTypes.map((type) => {
            const isChecked = Array.isArray(schemaPageTypes)
              ? schemaPageTypes.includes(type)
              : false;

            return (
              <div key={type} className={styles.schemaCheckboxesItem}>
                <input
                  disabled={!canUpdateSite || !effectiveSchemaSwitchEnabled}
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleTypeSelect(type)}
                  id={`schema-${type}`}
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor={`schema-${type}`}
                  className={styles.checkboxLabel}
                >
                  {formatLabel(type)}
                </label>
                <span>
                  {"("}
                  <Link
                    to={`/sites/${id}/pages?pageType=${encodeURIComponent(type)}`}
                    data-testid={`schema-link-${type}`}
                    onClick={(e) => handleLinkClick(e, type)}
                  >
                    {pagesStatusesDetail?.[type]}{" "}
                    <Icon
                      iconName="ExternalLinkIcon"
                      width="0.75rem"
                      height="0.75rem"
                    />
                  </Link>
                  {")"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SchemaSettings;
